import { useAuth0 } from '@auth0/auth0-react';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import TooManyRequestsError from '@/Utils/HTTP/Errors/TooManyRequestsError';
import http from '@/Utils/HTTP/NewHttpUtils';
import UIStrings from '@/Utils/UIStrings';

const RETRY_DELAY_MS = 65000; // 1 minute and 5 seconds as default ContactOut API rate limit is 150 requests per minute.
const MAX_RETRIES = 2;

export interface ContactInfoAvailability {
    personalEmailAvailable: boolean;
    workEmailAvailable: boolean;
    phoneNumberAvailable: boolean;
}

const getProfileContactInfoUrl = (linkedInUrl: string) =>
    `enriched-profiles/contact-info/status?profileUrl=${linkedInUrl}`;

export const useProfileContactInfo = (linkedInUrl?: string) => {
    const { getAccessTokenSilently } = useAuth0();
    const { cache } = useSWRConfig();

    return useSWRImmutable(
        linkedInUrl ? getProfileContactInfoUrl(linkedInUrl) : null,
        async (path) => {
            // there is a weird bug when you specifically resize MRT column
            // then useSWR with disabled automatic revalidation
            // (https://swr.vercel.app/docs/revalidation#disable-automatic-revalidations)
            // or useSWRImmutable will refetch instead of getting the cached
            // data. This won't happen if you change pagination, go to other
            // page and go back to refetch this profile, or etc as it should
            // be, only when resizing any one of the table's column
            const cachedCall = cache.get(path);
            if (cachedCall?.data) {
                return cachedCall.data as ContactInfoAvailability;
            }

            const response = await http.get<ContactInfoAvailability>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
        {
            onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
                if (error instanceof TooManyRequestsError) {
                    // retry 1 time in 65 seconds.
                    // if retry fails, show error toaster.
                    if (retryCount < MAX_RETRIES) {
                        setTimeout(() => revalidate({ retryCount }), RETRY_DELAY_MS);
                    } else {
                        toast.error(UIStrings.SomethingWentWrongPleaseTryAgainLater);
                    }
                }
            },
            onError: (error) => {
                // Disable global failed toaster to 429 responses.
                // 429 is handled inside onErrorRetry.
                if (!(error instanceof TooManyRequestsError)) {
                    toast.error(UIStrings.SomethingWentWrongPleaseTryAgainLater);
                }
            },
        },
    );
};

export const useCachedProfileContactInfos = (linkedInUrls: string[]) => {
    const { cache } = useSWRConfig();

    return useMemo(
        () =>
            linkedInUrls.map((url) => ({
                url,
                info: cache.get(getProfileContactInfoUrl(url))?.data as ContactInfoAvailability | undefined,
            })),
        [linkedInUrls],
    );
};

export const useRevealPersonalEmail = (linkedInUrl?: string) => {
    const { getAccessTokenSilently } = useAuth0();
    return useSWRImmutable(
        linkedInUrl ? `enriched-profiles/personal-email?profileUrl=${linkedInUrl}` : null,
        async (path) => {
            const response = await http.get<{ personalEmails: string[] }>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
        { revalidateOnMount: false },
    );
};

export const useRevealWorkEmail = (linkedInUrl?: string) => {
    const { getAccessTokenSilently } = useAuth0();
    return useSWRImmutable(
        linkedInUrl ? `enriched-profiles/work-email?profileUrl=${linkedInUrl}` : null,
        async (path) => {
            const response = await http.get<{ workEmails: string[] }>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
        { revalidateOnMount: false },
    );
};

export const useRevealPhoneNumber = (linkedInUrl?: string) => {
    const { getAccessTokenSilently } = useAuth0();
    return useSWRImmutable(
        linkedInUrl ? `enriched-profiles/phone-number?profileUrl=${linkedInUrl}` : null,
        async (path) => {
            const response = await http.get<{ phoneNumbers: string[] }>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
        { revalidateOnMount: false },
    );
};
